import React, { useState, useEffect } from 'react';
import { useSelectedTags } from '../utils/tagUtils';
import { filterDrawerStyles } from './FilterDrawer.styles';

/**
 * Bottom drawer for filtering thoughts by tags
 */
const FilterDrawer = ({ isVisible, onClose, onFilterChange, currentFilters, thoughts }) => {
    // Extract unique tags from thoughts
    const [availableTags, setAvailableTags] = useState([]);
    const { 
        selectedTags, 
        setSelectedTags, 
        toggleTagSelection, 
        clearTags, 
        hasSelectedTags 
    } = useSelectedTags(currentFilters?.tags || []);
    
    // Extract unique tags and count their frequency when thoughts change
    useEffect(() => {
        // Create a map to count tag occurrences
        const tagCounts = {};
        
        // Count occurrences of each tag
        thoughts.forEach(thought => {
            if (thought.tags && Array.isArray(thought.tags)) {
                thought.tags.forEach(tag => {
                    if (tag && tag.trim() !== '') {
                        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
                    }
                });
            }
        });
        
        // Convert to array and sort by frequency (descending)
        const sortedTags = Object.entries(tagCounts)
            .sort((a, b) => b[1] - a[1])  // Sort by count (descending)
            .map(([tag]) => tag);  // Only keep the tag name
            
        setAvailableTags(sortedTags);
    }, [thoughts]);
    
    // Reset selected filters when drawer opens
    useEffect(() => {
        if (isVisible) {
            setSelectedTags(currentFilters?.tags || []);
        }
    }, [isVisible, currentFilters, setSelectedTags]);
    
    // Apply the selected filters
    const applyFilters = () => {
        // Create a filter object with just tags property
        const newFilters = { tags: selectedTags };
        onFilterChange(newFilters);
        onClose();
    };
    
    // Check if we have any tags to show
    const hasTags = availableTags.length > 0;

    return (
        <>
            {/* Backdrop overlay */}
            <div
                style={{
                    ...filterDrawerStyles.backdrop,
                    opacity: isVisible ? 1 : 0,
                    pointerEvents: isVisible ? 'auto' : 'none',
                }}
                onClick={onClose}
            />

            {/* Bottom drawer */}
            <div style={{
                ...filterDrawerStyles.drawer,
                bottom: isVisible ? 0 : '-80vh',
            }}>
                {/* Header */}
                <div style={filterDrawerStyles.header.container}>
                    <div style={filterDrawerStyles.header.wrapper}>
                        <h2 style={filterDrawerStyles.header.title}>
                            Filter by Tags
                        </h2>
                        
                        {hasSelectedTags && (
                            <button
                                onClick={clearTags}
                                style={filterDrawerStyles.header.clearButton}
                            >
                                Clear All
                            </button>
                        )}
                    </div>
                </div>

                {hasTags ? (
                    <div style={filterDrawerStyles.tagsSection.container}>
                        {/* Tags section */}
                        <div style={filterDrawerStyles.tagsSection.wrapper}>
                            {availableTags.map(tag => (
                                <button
                                    key={tag}
                                    onClick={() => toggleTagSelection(tag)}
                                    style={filterDrawerStyles.tagsSection.tagButton(selectedTags.includes(tag))}
                                >
                                    {tag}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div style={filterDrawerStyles.emptyState}>
                        <p>No tags available for filtering</p>
                    </div>
                )}

                {/* Action buttons */}
                <div style={filterDrawerStyles.actions.container}>
                    <button
                        onClick={applyFilters}
                        style={filterDrawerStyles.actions.applyButton}
                    >
                        Apply
                    </button>
                </div>
            </div>
        </>
    );
};

export default FilterDrawer; 